import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { Badge, Fab } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { FloatingActionSlot } from '../FloatingActionStack';
import MessageToast from '../MessageToast';
import { Palette } from 'Palette';
import { useFidesChat } from '../../context/FidesChatContext';
import useFidesChatInbox from '../../hooks/admin/useFidesChatInbox';
import BackendService from '../../services/admin/BackendService';
import { FidesChatBubbleNotification } from './FidesChatBubbleNotification';
import { FidesChatInboxPanel } from './FidesChatInboxPanel';
import { adminExternalId } from './fidesChatTrayUtils';
import { useFidesChatBubbleNotifications } from './useFidesChatBubbleNotifications';

const FidesChatTray: React.FC = () => {
  const chat = useFidesChat();
  const [currentAdminExternalId, setCurrentAdminExternalId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [dismissedError, setDismissedError] = useState<string | null>(null);
  // Firestore/Firebase chat isn't configured in the E2E environment (no
  // FIREBASE_* env vars — see FirestoreChatService.ts) — every snapshot
  // attempt and poll would otherwise fail forever (real backend 500s,
  // logged as "Firestore chat not configured"), keeping the admin page in
  // continuous re-render/retry churn that never settles. That instability
  // was masquerading as an E2E test timeout (a screenshot waiting on a
  // never-stable element) with no obvious connection to chat at all.
  const chatEnabled = process.env.REACT_APP_PRODUCT_NAME !== 'Fides E2E';
  const { conversations, loading, error, refresh } = useFidesChatInbox(chatEnabled);

  useEffect(() => {
    void new BackendService()
      .getCurrentUser()
      .then((admin) => setCurrentAdminExternalId(adminExternalId(admin)))
      .catch(() => setCurrentAdminExternalId(null));
  }, []);

  const { bubble, dismissBubble } = useFidesChatBubbleNotifications({
    conversations,
    loading,
    open,
    isPanelOpen: Boolean(chat?.isPanelOpen),
    openConversationId: chat?.openConversationId,
    currentAdminExternalId,
  });

  useEffect(() => {
    if (chat?.isPanelOpen) {
      setOpen(false);
      dismissBubble();
    }
  }, [chat?.isPanelOpen, dismissBubble]);

  useEffect(() => {
    if (open) {
      dismissBubble();
      refresh();
    }
  }, [open, refresh, dismissBubble]);

  const unreadCount = useMemo(
    () => conversations.filter((conversation) => conversation.unreadByAdmin).length,
    [conversations],
  );

  const hideTrayFab = Boolean(chat?.isPanelOpen);

  if (!chat) {
    return null;
  }

  return (
    <>
      {error && error !== dismissedError && (
        <MessageToast
          type="error"
          message={error}
          open={true}
          onClose={() => setDismissedError(error)}
        />
      )}

      <FidesChatBubbleNotification
        bubble={bubble}
        onOpenConversation={(conversationId) => chat.openConversation(conversationId)}
        onDismiss={dismissBubble}
      />

      <FidesChatInboxPanel
        open={open}
        loading={loading}
        conversations={conversations}
        currentAdminExternalId={currentAdminExternalId}
        onClose={() => setOpen(false)}
        onOpenConversation={(conversationId) => {
          chat.openConversation(conversationId);
          setOpen(false);
        }}
      />

      {!hideTrayFab && (
        <FloatingActionSlot order={20}>
          <Fab
            aria-label={open ? 'Close investor chat inbox' : 'Open investor chat inbox'}
            onClick={() => setOpen((current) => !current)}
            sx={{
              backgroundColor: Palette.default.buttonBg,
              color: '#fff',
              '&:hover': { backgroundColor: Palette.default.text },
              boxShadow: open ? 2 : 6,
            }}
          >
            <Badge color="error" badgeContent={unreadCount} max={99} invisible={unreadCount === 0}>
              {open ? <CloseIcon /> : <ChatBubbleOutlineIcon />}
            </Badge>
          </Fab>
        </FloatingActionSlot>
      )}
    </>
  );
};

export default FidesChatTray;
